// components/schedule/UncompletedConstructionDialog.tsx
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableBody, TableCell, TableHead, TableRow, Box,
  TextField, InputAdornment, CircularProgress, IconButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  EventAvailable as EventIcon
} from '@mui/icons-material';
import { scheduleService } from '../../services/schedule';

interface UncompletedConstructionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectConstruction: (constructionId: number, date: Date) => void;
}

const UncompletedConstructionDialog: React.FC<UncompletedConstructionDialogProps> = ({
  open, onClose, onSelectConstruction
}) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [constructions, setConstructions] = useState<any[]>([]);
  const [filteredConstructions, setFilteredConstructions] = useState<any[]>([]);
  
  useEffect(() => {
    if (open) {
      fetchUncompletedConstructions();
    }
  }, [open]);
  
  useEffect(() => {
    // 検索フィルター適用
    if (searchTerm.trim() === '') {
      setFilteredConstructions(constructions);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = constructions.filter(c => 
        c.Site_Name.toLowerCase().includes(term) ||
        (c.Customer_Name && c.Customer_Name.toLowerCase().includes(term)) ||
        (c.Address && c.Address.toLowerCase().includes(term))
      );
      setFilteredConstructions(filtered);
    }
  }, [searchTerm, constructions]);
  
  const fetchUncompletedConstructions = async () => {
    setLoading(true);
    try {
      // 未完了工事取得API (Is_Completed=0のものを取得)
      const data = await scheduleService.getConstructions();
      const uncompleted = data.filter((c: any) => c.Is_Completed === 0);
      setConstructions(uncompleted);
      setFilteredConstructions(uncompleted);
    } catch (error) {
      console.error('Error fetching uncompleted constructions:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const handleScheduleConstruction = (constructionId: number) => {
    onSelectConstruction(constructionId, new Date());
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>未完了工事一覧</DialogTitle>
      
      <DialogContent>
        <Box mb={2}>
          <TextField
            fullWidth
            placeholder="現場名、顧客名、住所で検索"
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>現場名</TableCell>
                <TableCell>顧客名</TableCell>
                <TableCell>住所</TableCell>
                <TableCell width="100">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredConstructions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    未完了工事がありません
                  </TableCell>
                </TableRow>
              ) : (
                filteredConstructions.map(construction => (
                  <TableRow key={construction.ID}>
                    <TableCell>{construction.Site_Name}</TableCell>
                    <TableCell>{construction.Customer_Name}</TableCell>
                    <TableCell>{construction.Address}</TableCell>
                    <TableCell>
                      <Tooltip title="予定登録">
                        <IconButton 
                          size="small"
                          color="primary"
                          onClick={() => handleScheduleConstruction(construction.ID)}
                        >
                          <EventIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UncompletedConstructionDialog;